import { useRecentEvents } from "../api/hooks";
import type { Migration, MigrationEvent, MigrationState } from "../api/types";
import { formatDateTimeShort } from "../api/types";
import { formatRelativePast } from "../i18n/relativeTime";
import { t, tDynamic } from "../i18n/t";

function payloadOf(event: MigrationEvent): Record<string, unknown> {
  const payload = (event as { payload?: unknown }).payload;
  return typeof payload === "object" && payload !== null ? (payload as Record<string, unknown>) : {};
}

/**
 * Why the migration stopped, in the worker's own words: the `failed` transition's note, else the first
 * destination error it recorded. `null` when the newest event is not a failure (the migration is not
 * stopped, or the event list has not loaded yet).
 */
export function failureReason(events: readonly MigrationEvent[]): string | null {
  const newest = events[0];
  if (newest === undefined || newest.to_state !== "failed") {
    return null;
  }
  if (typeof newest.note === "string" && newest.note !== "") {
    return newest.note;
  }
  const errors = payloadOf(newest).errors;
  if (Array.isArray(errors)) {
    const first = errors.find((value): value is string => typeof value === "string" && value !== "");
    if (first !== undefined) return first;
  }
  return null;
}

/** The state a retry restarts from (the failure event's own `retry_target`), when the worker recorded one. */
export function retryTarget(events: readonly MigrationEvent[]): MigrationState | null {
  const newest = events[0];
  if (newest === undefined || newest.to_state !== "failed") {
    return null;
  }
  const target = payloadOf(newest).retry_target;
  return typeof target === "string" && target !== "" ? (target as MigrationState) : null;
}

/**
 * xmg-webux (first staging trial, defect 5): a compact "what is happening now" strip on every migration
 * screen -- the current step, when it last changed, the last few transitions with their times and, for a
 * stopped migration, the reason. It polls with the migration (`useRecentEvents` uses the same §A9.8
 * interval), so a dry run finishing or failing shows up here without a reload.
 */
export function LiveActivity({
  id,
  migration,
}: {
  id: string;
  migration: Migration | undefined;
}): JSX.Element {
  const state = migration?.state as MigrationState | undefined;
  const recent = useRecentEvents(id === "" ? null : id, state);
  const events = recent.data ?? [];
  const reason = failureReason(events);
  return (
    <section className="liveactivity" aria-label={t("activity.title")} data-testid="live-activity">
      <div className="liveactivity__now">
        <span className="eyebrow">{t("activity.now")}</span>{" "}
        <strong data-testid="live-activity-state">
          {state === undefined ? t("common.unknown") : tDynamic("state.", state)}
        </strong>
        {migration?.updated_at === undefined ? null : (
          <span className="muted">
            {" "}
            · {t("activity.updated", { when: formatRelativePast(migration.updated_at) })}
          </span>
        )}
      </div>
      {reason === null ? null : (
        <p className="liveactivity__reason" data-testid="live-activity-reason">
          {t("activity.stopped_because", { reason })}
        </p>
      )}
      {events.length === 0 ? null : (
        // xmg-actions-first: the last few steps are one click away; the strip itself stays one line, so the
        // first screen holds the questions and the action, not the history.
        <details className="liveactivity__more" open={reason === null ? undefined : true}>
          <summary>{t("activity.recent_count", { n: events.length })}</summary>
          <ol className="liveactivity__list" aria-label={t("activity.recent")}>
            {events.map((event) => (
              <li key={event.id} data-testid={`live-activity-event-${event.id}`}>
                <span className="mono muted">{formatDateTimeShort(event.created_at)}</span>{" "}
                {t("status.transition", {
                  from: event.from_state === null ? t("common.none") : tDynamic("state.", event.from_state),
                  to: tDynamic("state.", event.to_state),
                })}
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  );
}
